// src/pages/LogsPage.js
import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import { collectionGroup, getDocs, query, orderBy } from "firebase/firestore";
import { firestore } from "../firebase";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import Footer from "../components/Footer";

const LogsPage = () => {
  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [logType, setLogType] = useState("all");

  // Fetch logs from Firestore using collectionGroup query.
  // We assume that logs are stored under each rental shop in a subcollection called "logs"
  // and that each log document contains fields like `timestamp`, `type`, `message`, and optionally `businessName`.
  const fetchLogs = async () => {
    // const q = query(collectionGroup(firestore, "logs"));
    const q = query(
      collectionGroup(firestore, "logs"),
      orderBy("timestamp", "desc"),
      orderBy("vehicleId", "asc"),
      orderBy("__name__", "asc")
    );

    const snapshot = await getDocs(q);
    const logs = [];
    snapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return logs;
  };

  const { data: logs, isLoading } = useQuery({
    queryKey: ["logs"],
    queryFn: fetchLogs,
  });

  // Filter logs based on search and log type.
  const filteredLogs = logs
    ? logs.filter((log) => {
        let matchesType = true;
        if (logType !== "all") {
          // We assume each log document has a field 'type'
          matchesType = log.type === logType;
        }
        let matchesSearch = true;
        if (searchTerm) {
          // We assume logs may contain a field 'businessName'
          matchesSearch = log.businessName
            ? log.businessName.toLowerCase().includes(searchTerm.toLowerCase())
            : false;
        }
        return matchesType && matchesSearch;
      })
    : [];

  return (
    <>
      <Header />
      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Logs
        </Typography>
        <Grid container spacing={2}>
          {/* Left: Logs list */}
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                p: 2,
                height: "70vh",
                overflowY: "auto",
              }}
            >
              {isLoading ? (
                <Typography>Loading logs...</Typography>
              ) : filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <Card key={log.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary">
                        {new Date(
                          log.timestamp?.toDate
                            ? log.timestamp.toDate()
                            : log.timestamp
                        ).toLocaleString()}
                      </Typography>
                      <Typography variant="body1">{log.message}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Type: {log.type}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography>No logs found.</Typography>
              )}
            </Box>
          </Grid>
          {/* Right: Filter panel */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <TextField
                label="Search Business"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel id="log-type-label">Log Type</InputLabel>
                <Select
                  labelId="log-type-label"
                  label="Log Type"
                  value={logType}
                  onChange={(e) => setLogType(e.target.value)}
                >
                  <MenuItem value="all">All Logs</MenuItem>
                  <MenuItem value="bookingCreation">Booking Creation</MenuItem>
                  <MenuItem value="cancellation">Cancellation</MenuItem>
                  {/* Add more log types as needed */}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
};

export default LogsPage;
